import { apiRequest, ApiResponse } from '../request'

export enum ShareableModule {
    CLIENT = 'client',
    PRODUCT = 'product',
    PROFESSIONAL = 'professional',
    CATALOG = 'catalog',
    PRICE_TABLE = 'price_table',
    MEDICATION = 'medication',
    ROOM = 'room',
    SPECIALTY = 'specialty',
    HEALTH_PLAN = 'health_plan',
    ANAMNESIS_TEMPLATE = 'anamnesis_template',
    MEDICAL_REPORT_TEMPLATE = 'medical_report_template',
    INFORMED_CONSENT_TEMPLATE = 'informed_consent_template',
    DEPARTMENT = 'department',
    ATTENDANT = 'attendant',
    INTEGRATION = 'integration',
    CONSULTATION = 'consultation',
    TRIAGE = 'triage',
    EXAM = 'exam',
    PRESCRIPTION = 'prescription',
    PROCEDURE = 'order',
    EMPLOYEE = 'employee',
    PARTNER = 'partner',
    PRODUCT_CATEGORY = 'product_category',
    TISS_CONFIG = 'tiss_config',
    PACS_SERVER = 'pacs_server',
    PHARMACY_INTEGRATION = 'pharmacy_integration',
    RNDS_CONFIG = 'rnds_config',
    LAB_INTEGRATION = 'lab_integration',
}

export interface ShareDataDto {
    module: ShareableModule
    recordId: number
    targetCompanyIds: number[]
    note?: string
}

export interface ShareResultItem {
    companyId: number
    companyName: string
    success: boolean
    newRecordId?: number
    error?: string
}

export interface ShareResultDto {
    success: boolean
    results: ShareResultItem[]
}

export interface ShareableModuleInfo {
    module: ShareableModule
    label: string
}

export async function shareData(data: ShareDataDto): Promise<ApiResponse<ShareResultDto>> {
    return apiRequest<ShareResultDto>('share', { data }, 'post')
}

export interface ImportDataDto {
    module: ShareableModule
    sourceCompanyId: number
    recordIds?: number[]
}

export async function importData(data: ImportDataDto): Promise<ApiResponse<ShareResultDto>> {
    return apiRequest<ShareResultDto>('share/import', { data }, 'post')
}

export async function getShareableModules(): Promise<ApiResponse<ShareableModuleInfo[]>> {
    return apiRequest<ShareableModuleInfo[]>('share/modules', {}, 'get')
}

export interface FileImportEnqueuedDto {
    jobId: string
}

export async function importFromFile(
    file: File,
    module: ShareableModule,
    options?: { partnerId?: number; defaultMarkupPercentage?: number },
): Promise<ApiResponse<FileImportEnqueuedDto>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('module', module)
    if (options?.partnerId) {
        formData.append('partnerId', String(options.partnerId))
    }
    if (options?.defaultMarkupPercentage != null) {
        formData.append('defaultMarkupPercentage', String(options.defaultMarkupPercentage))
    }

    return apiRequest<FileImportEnqueuedDto>(
        'share/import-file',
        { data: formData, formData: true },
        'post'
    )
}

// ─── Importação Pendente (Revisão) ───────────────────────────

export type ImportItemType = 'PROCEDURE' | 'CONSULTATION' | 'EXAM'

export interface ImportPendingRecord {
    name: string
    tussCode?: string
    type?: string
    description?: string
    itemType?: ImportItemType
    price: number
    markupPercentage?: number | null
    sellingPrice?: number | null
    existsInDb: boolean
    existingId?: number | null
    currentPrice?: number | null
    currentMarkup?: number | null
    currentSellingPrice?: number | null
    // Campos extras de procedimento
    defaultDuration?: number | null
    preInstructions?: string | null
    postInstructions?: string | null
    // Campos extras de exame
    maxDiscount?: number | null
    instructions?: string | null
}

export interface ImportPendingResponse {
    id: number
    module: string
    partnerId: number
    partnerName: string
    defaultMarkupPercentage?: number | null
    totalRecords: number
    status: string
    records: ImportPendingRecord[]
    createdAt: string
    expiresAt: string
}

export interface ConfirmImportResult {
    success: boolean
    totalFound: number
    imported: number
    created: number
    updated: number
    errors: { index: number; error: string }[]
}

export async function getImportPending(id: number): Promise<ApiResponse<ImportPendingResponse>> {
    return apiRequest<ImportPendingResponse>(`share/import-pending/${id}`, {}, 'get')
}

export async function confirmImportPending(
    id: number,
    records: ImportPendingRecord[],
): Promise<ApiResponse<ConfirmImportResult>> {
    return apiRequest<ConfirmImportResult>(
        `share/import-pending/${id}/confirm`,
        { data: { records } },
        'post'
    )
}

export async function cancelImportPending(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`share/import-pending/${id}`, {}, 'delete')
}
